import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { PageSpinner } from '../../../components/PageSpinner';
import { StatusBadge } from '../../../components/StatusBadge';
import { applicationsApi } from '../../../features/applications/applications.api';
import { useSessionQuery as useQuery } from '../../../hooks/useSessionQuery';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { paths, routeTo } from '../../../routes/paths';
import styles from '../AppPages.module.css';

export function ApplicationsListPage(): React.JSX.Element {
  const [page, setPage] = useState(1);
  const applications = useQuery({
    queryKey: ['applications', 'list', page],
    queryFn: () => applicationsApi.listForCustomer({ page, pageSize: 10 }),
  });

  const items = applications.data?.items ?? [];

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Applications</h1>
        <Link to={paths.customerApply}>Start application</Link>
      </header>

      <section className={styles.card} aria-labelledby="applications-heading">
        <h2 className={styles.cardTitle} id="applications-heading">
          In progress
        </h2>

        {applications.isPending && <PageSpinner label="Loading applications" />}
        {applications.isError && <Alert tone="error">{toDisplayMessage(applications.error)}</Alert>}

        {applications.isSuccess && items.length === 0 && (
          <p className={styles.empty}>No applications in progress.</p>
        )}

        {applications.isSuccess && items.length > 0 && (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Status</th>
                  <th scope="col">Product price</th>
                  <th scope="col">Updated</th>
                  <th scope="col">
                    <span className={styles.visuallyHidden}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((application) => (
                  <tr key={application.id}>
                    <td>
                      <Link to={routeTo.customerApplication(application.id)}>
                        {application.productNameSnapshot ??
                          application.requestedTerms?.productName ??
                          'Solar application'}
                      </Link>
                    </td>
                    <td>
                      <StatusBadge status={application.status} />
                    </td>
                    <td>{formatNgn(application.productPrice)}</td>
                    <td>{new Date(application.updatedAt).toLocaleString()}</td>
                    <td>
                      {(application.status === 'DRAFT' ||
                        application.status === 'CHANGES_REQUESTED') && (
                        <Link to={routeTo.customerApplicationEdit(application.id)}>
                          Continue application
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {applications.isSuccess && applications.data.pagination.totalPages > 1 && (
          <div className={styles.actions} aria-label="Application pages">
            <Button
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </Button>
            <span>
              Page {applications.data.pagination.page} of {applications.data.pagination.totalPages}
            </span>
            <Button
              variant="secondary"
              disabled={page >= applications.data.pagination.totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
